import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2, Download, Copy, Share2, ArrowLeft, Check } from "lucide-react";
import html2canvas from "html2canvas";
import { api } from "../lib/api";
import { PostmanIcon } from "../components/PostmanIcon";

const langFontClass = (lang) => (lang && lang.startsWith("zh") ? "font-cn" : "font-display");

export default function EchoPage() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const [echo, setEcho] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | error | not-yet
  const [errMsg, setErrMsg] = useState("");
  const [sharing, setSharing] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    api
      .get(`/capsules/${id}/echo`)
      .then((r) => {
        if (cancelled) return;
        setEcho(r.data);
        // Sync UI language to the echo's language so the page reads naturally
        if (r.data?.language_code && r.data.language_code !== i18n.language) {
          i18n.changeLanguage(r.data.language_code);
        }
        setStatus("ready");
      })
      .catch((e) => {
        if (cancelled) return;
        const code = e.response?.status;
        if (code === 409) {
          setStatus("not-yet");
        } else {
          setStatus("error");
          setErrMsg(e.response?.data?.detail || String(e));
        }
      });
    return () => { cancelled = true; };
  }, [id, i18n]);

  const shareUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    // Use the backend share endpoint so social platforms (Threads, WhatsApp,
    // LINE, X/Twitter, FB) can crawl the OG meta tags and render a rich
    // dynamic preview card. Human visitors are redirected to /echo/{id}.
    const backend = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/$/, "");
    return `${backend}/api/share/echo/${id}`;
  }, [id]);

  const renderCardToBlob = async () => {
    if (!cardRef.current) return null;
    const canvas = await html2canvas(cardRef.current, {
      backgroundColor: "#F9F6F0",
      scale: 2,
      useCORS: true,
      logging: false,
    });
    return await new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/png", 0.95));
  };

  const handleShare = async () => {
    if (!echo) return;
    setSharing(true);
    try {
      const blob = await renderCardToBlob();
      if (!blob) return;
      const file = new File([blob], `miprose-echo-${echo.id}.png`, { type: "image/png" });
      // Mobile native share with files, when supported
      if (navigator.canShare && navigator.canShare({ files: [file] }) && navigator.share) {
        try {
          await navigator.share({
            files: [file],
            title: t("echo.share_native_title"),
            text: t("echo.share_native_text"),
            url: shareUrl,
          });
          return;
        } catch (e) {
          // user dismissed; fall through to download
        }
      }
      // Desktop fallback: download
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `miprose-echo.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setSharing(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2400);
    } catch (e) {
      // ignore
    }
  };

  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center text-[var(--ink-soft)] italic gap-3" data-testid="echo-loading">
        <Loader2 className="animate-spin" /> {t("echo.loading")}
      </div>
    );
  }

  if (status === "not-yet") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center" data-testid="echo-not-yet">
        <PostmanIcon size={56} />
        <h1 className="font-display text-3xl md:text-4xl text-[var(--ink)] mt-6 mb-3">{t("echo.not_yet_title")}</h1>
        <p className="text-[15px] italic text-[var(--ink-soft)] max-w-md">{t("echo.not_yet_body")}</p>
        <Link to="/" className="mt-10 inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] text-[var(--wax)] hover:text-[var(--ink)]">
          <ArrowLeft size={12} /> {t("echo.back_home")}
        </Link>
      </div>
    );
  }

  if (status === "error" || !echo) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center" data-testid="echo-error">
        <h1 className="font-display text-2xl text-[var(--ink)] mb-3">{t("echo.error_title")}</h1>
        <p className="text-[14px] italic text-[var(--ink-soft)]">{errMsg || t("echo.error_body")}</p>
      </div>
    );
  }

  const isCJK = (echo.language_code || "en").startsWith("zh");
  const fontClass = langFontClass(echo.language_code);

  return (
    <div className="min-h-screen px-6 py-12 md:py-20" data-testid="echo-page">
      <div className="max-w-2xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] text-[var(--ink-soft)] hover:text-[var(--wax)] mb-10">
          <ArrowLeft size={12} /> {t("echo.back_home")}
        </Link>

        {/* Header */}
        <div className="text-center mb-12">
          <PostmanIcon size={64} className="mx-auto mb-4" />
          <p className="text-[10px] uppercase tracking-[0.42em] text-[var(--ink-mute)] mb-2">
            {t("echo.brand_kicker")}
          </p>
          <h1 className={`${fontClass} text-3xl md:text-4xl text-[var(--ink)] tracking-tight`} data-testid="echo-title">
            {t("echo.page_title", { days: echo.duration_days })}
          </h1>
          <p className="mt-3 text-[14px] italic text-[var(--ink-soft)]">{t("echo.privacy_assurance")}</p>
        </div>

        {/* Golden quote (main) — marks bracket the text INLINE, editorial style */}
        <blockquote
          data-testid="echo-golden-quote"
          className={`${fontClass} my-12 px-8 md:px-12 py-10 border-l-[3px] border-[var(--wax)] bg-[var(--bg-paper-deep)] italic text-[20px] md:text-[24px] leading-[1.85] text-[var(--ink)] relative`}
        >
          <span
            aria-hidden
            className="text-[var(--wax)] select-none not-italic"
            style={{ fontWeight: 500, fontSize: "1.08em", marginRight: isCJK ? "0.02em" : "0.08em", verticalAlign: isCJK ? "baseline" : "-0.05em" }}
          >
            {isCJK ? "「" : "“"}
          </span>
          {echo.golden_quote}
          <span
            aria-hidden
            className="text-[var(--wax)] select-none not-italic"
            style={{ fontWeight: 500, fontSize: "1.08em", marginLeft: isCJK ? "0.02em" : "0.04em", verticalAlign: isCJK ? "baseline" : "-0.05em" }}
          >
            {isCJK ? "」" : "”"}
          </span>
        </blockquote>

        {/* Full reply (expandable, soft) */}
        <details className="my-10 group" data-testid="echo-full-reply">
          <summary className="cursor-pointer list-none inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.24em] text-[var(--ink-soft)] hover:text-[var(--wax)] transition-colors">
            <span className="group-open:hidden">{t("echo.show_full")}</span>
            <span className="hidden group-open:inline">{t("echo.hide_full")}</span>
          </summary>
          <div className={`${fontClass} mt-6 whitespace-pre-wrap text-[16px] md:text-[17px] leading-[1.95] text-[var(--ink)]`}>
            {echo.ai_response}
          </div>
        </details>

        {/* Share block */}
        <div className="mt-16 pt-10 border-t border-dashed border-[var(--rule,#C9BFB0)] text-center">
          <h2 className={`${fontClass} text-2xl md:text-[28px] text-[var(--ink)] mb-3`}>{t("echo.share_title")}</h2>
          <p className="text-[14px] italic text-[var(--ink-soft)] max-w-md mx-auto mb-8">{t("echo.share_subtext")}</p>

          <div className="flex flex-wrap items-center justify-center gap-3">
            <button
              data-testid="echo-share-btn"
              onClick={handleShare}
              disabled={sharing}
              className="inline-flex items-center gap-2 px-6 py-3 bg-[var(--ink)] text-[var(--bg-paper)] text-[12px] uppercase tracking-[0.22em] hover:bg-[var(--wax)] transition-colors disabled:opacity-50"
            >
              {sharing ? <Loader2 size={14} className="animate-spin" /> : <Share2 size={14} />}
              {sharing ? t("echo.preparing") : t("echo.share_cta")}
            </button>
            <button
              data-testid="echo-download-btn"
              onClick={handleShare}
              disabled={sharing}
              className="inline-flex items-center gap-2 px-6 py-3 border border-[var(--ink)] text-[var(--ink)] text-[12px] uppercase tracking-[0.22em] hover:bg-[var(--ink)] hover:text-[var(--bg-paper)] transition-colors disabled:opacity-50"
            >
              <Download size={14} /> {t("echo.download_card")}
            </button>
            <button
              data-testid="echo-copy-link"
              onClick={handleCopyLink}
              className="inline-flex items-center gap-2 px-6 py-3 border border-[var(--ink-mute)] text-[var(--ink-soft)] text-[12px] uppercase tracking-[0.22em] hover:border-[var(--ink)] hover:text-[var(--ink)] transition-colors"
            >
              {linkCopied ? <Check size={14} /> : <Copy size={14} />}
              {linkCopied ? t("echo.link_copied") : t("echo.copy_link")}
            </button>
          </div>
        </div>

        {/* OFF-SCREEN render target for html2canvas — never visible to user */}
        <div style={{ position: "fixed", left: "-10000px", top: 0, pointerEvents: "none" }} aria-hidden>
          <ShareCard ref={cardRef} echo={echo} t={t} fontClass={fontClass} isCJK={isCJK} />
        </div>
      </div>
    </div>
  );
}

/* ---------- The downloadable / shareable square image (1080 x 1080) ---------- */
const ShareCard = React.forwardRef(({ echo, t, fontClass, isCJK }, ref) => {
  return (
    <div
      ref={ref}
      style={{
        width: 1080,
        height: 1080,
        background:
          "radial-gradient(120% 80% at 50% 0%, #FBF8F2 0%, #F3EFE5 100%)",
        color: "#1C1A17",
        padding: "120px 110px",
        boxSizing: "border-box",
        position: "relative",
        fontFamily: isCJK
          ? "'Noto Serif TC', 'Noto Serif SC', 'Songti SC', serif"
          : "'Playfair Display', 'Cormorant Garamond', Georgia, serif",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      {/* Subtle paper grain via repeating noise dots */}
      <svg width="0" height="0" style={{ position: "absolute" }}>
        <filter id="paperNoise">
          <feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="2" />
          <feColorMatrix values="0 0 0 0 0.10 0 0 0 0 0.09 0 0 0 0 0.08 0 0 0 0.06 0" />
        </filter>
      </svg>
      <div
        style={{
          position: "absolute",
          inset: 0,
          filter: "url(#paperNoise)",
          opacity: 0.55,
          pointerEvents: "none",
        }}
      />

      {/* Header */}
      <div style={{ textAlign: "center", position: "relative" }}>
        <div style={{ display: "inline-block", marginBottom: 28 }}>
          <PostmanIconBig />
        </div>
        <div
          style={{
            fontSize: 18,
            letterSpacing: "0.42em",
            textTransform: "uppercase",
            color: "#8B8275",
          }}
        >
          MiProse
        </div>
      </div>

      {/* Main quote — quotation marks bracket the text inline (left/right),
          editorial book-pull-quote style. */}
      <div style={{ position: "relative", textAlign: "center", padding: "0 56px" }}>
        <div
          className={fontClass}
          style={{
            fontStyle: "italic",
            fontSize: isCJK ? 46 : 50,
            lineHeight: 1.6,
            color: "#1C1A17",
            letterSpacing: isCJK ? "0.02em" : "-0.005em",
            maxWidth: 880,
            margin: "0 auto",
            wordBreak: "break-word",
            textIndent: 0,
          }}
        >
          <span
            aria-hidden
            style={{
              color: "#8A3A33",
              fontWeight: 500,
              fontStyle: "normal",
              fontSize: "1.08em",
              marginRight: isCJK ? "0.02em" : "0.08em",
              verticalAlign: isCJK ? "baseline" : "-0.05em",
              fontFamily: isCJK ? "'Noto Serif TC', serif" : "'Playfair Display', Georgia, serif",
            }}
          >
            {isCJK ? "「" : "“"}
          </span>
          {echo.golden_quote}
          <span
            aria-hidden
            style={{
              color: "#8A3A33",
              fontWeight: 500,
              fontStyle: "normal",
              fontSize: "1.08em",
              marginLeft: isCJK ? "0.02em" : "0.04em",
              verticalAlign: isCJK ? "baseline" : "-0.05em",
              fontFamily: isCJK ? "'Noto Serif TC', serif" : "'Playfair Display', Georgia, serif",
            }}
          >
            {isCJK ? "」" : "”"}
          </span>
        </div>
      </div>

      {/* Footer */}
      <div style={{ textAlign: "center", position: "relative" }}>
        <div
          style={{
            fontSize: 18,
            color: "#5C564C",
            fontStyle: "italic",
            marginBottom: 16,
          }}
        >
          {t("echo.share_card_attribution", { days: echo.duration_days })}
        </div>
        <div
          style={{
            fontSize: 16,
            letterSpacing: "0.36em",
            textTransform: "uppercase",
            color: "#8A3A33",
          }}
        >
          miprose.com
        </div>
      </div>
    </div>
  );
});
ShareCard.displayName = "ShareCard";

const PostmanIconBig = () => (
  <svg width="96" height="96" viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <path d="M54 16 a8 8 0 0 0 8 8 a10 10 0 1 1 -8 -8 z" fill="#1C1A17" opacity="0.85" />
    <circle cx="22" cy="18" r="1.1" fill="#8A3A33" />
    <circle cx="34" cy="12" r="0.9" fill="#8A3A33" opacity="0.7" />
    <circle cx="14" cy="28" r="0.8" fill="#1C1A17" opacity="0.45" />
    <rect x="12" y="34" width="56" height="32" rx="1.5" fill="#F9F6F0" stroke="#1C1A17" strokeWidth="1.4" />
    <path d="M12 34 L40 54 L68 34" fill="none" stroke="#1C1A17" strokeWidth="1.4" />
    <circle cx="40" cy="54" r="4" fill="#8A3A33" />
    <circle cx="40" cy="54" r="1.5" fill="#F9F6F0" opacity="0.45" />
  </svg>
);
